export const CHAPTERS = [
  [
    'people',
    'Your people',
    'A plan for the people who count.',
    'Start with you, then include your partner, children and anyone you financially support.',
  ],
  [
    'income',
    'Your income',
    'What comes in makes things possible.',
    'Bring together regular income. We’ll ask about payroll deductions only for salary.',
  ],
  [
    'spending',
    'Your spending',
    'Make room for everyday life.',
    'Separate housing, essentials and annual costs. Rough estimates are a useful starting point.',
  ],
  [
    'balance',
    'What you own & owe',
    'See the picture you’ve already built.',
    'Include savings, investments and outstanding loans. Current values are enough.',
  ],
  [
    'protection',
    'Your protection',
    'Look after what matters.',
    'Tell us about existing cover and who it protects. It’s fine if you need to check a policy.',
  ],
  [
    'future',
    'Your future',
    'Give your money something to work towards.',
    'A home, education, retirement or something entirely your own. Start with what you know.',
  ],
]
export const ANNUAL_COSTS = [
  ['vacation', 'Travel and holidays', 'Trips and breaks across the year.'],
  [
    'childrenFees',
    'Current school or college fees',
    'All your children together, including tuition, coaching and activities.',
  ],
  ['carInsurance', 'Vehicle insurance', 'Annual premiums for all vehicles.'],
  ['medicalPremium', 'Health insurance premiums', 'What you pay each year, not the sum insured.'],
  ['termPremium', 'Term insurance premiums', 'What you pay each year for life cover.'],
  ['otherInsurance', 'Other insurance premiums', 'For example, home insurance.'],
]
let sequence = 0
export const newItem = (section, relationship = '') => ({
  id: `${Date.now()}-${sequence++}`,
  ...{
    members: { name: '', relationship, dob: '', occupation: '', dependent: '' },
    salaries: { member: '', incomeType: 'Salary', gross: '', tax: '', pf: '', nps: '' },
    holdings: { name: 'Mutual funds', value: '', location: '' },
    loans: { type: 'Home loan', amount: '', emi: '', monthsLeft: '', details: '' },
    assetGoals: {
      kind: 'Other',
      name: '',
      year: '',
      amount: '',
      saved: '',
      child: '',
      studyLocation: '',
    },
    extraYearlyExpenses: { name: '', amount: '' },
  }[section],
})
export const createPlanner = () => ({
  version: 2,
  schemaVersion: 3,
  questionnaire2Version: 2,
  members: [newItem('members', 'Self')],
  salaries: [],
  holdings: [],
  loans: [],
  assetGoals: [],
  extraYearlyExpenses: [],
  monthlyExpenses: { housing: '', household: '', rent: '', emi: '', parents: '', children: '' },
  yearlyExpenses: { ...Object.fromEntries(ANNUAL_COSTS.map(([key]) => [key, ''])), lifestyle: '' },
  annualSelected: [],
  annualChoice: '',
  parentsSupport: '',
  insurance: {
    corporateMedical: '',
    privateMedical: '',
    term: '',
    corporateCoverage: '',
    privateCoverage: '',
    termCoverage: '',
    corporateWho: '',
    privateWho: '',
    termWho: '',
    privateBasis: '',
    corporateBasis: '',
  },
  retirement: {
    monthly: '',
    style: '',
    estimateConfirmed: false,
    vacation: '',
    annual: '',
    monthlyIncrease: 5,
    vacationIncrease: 5,
    annualIncrease: 5,
  },
  assumptions: {
    planTill: 90,
    equityReturns: 12,
    debtReturns: 7,
    essentialInflation: 5,
    nonEssentialInflation: 5,
    educationInflation: 8,
    sipYearlyIncrease: 8,
  },
  estimates: null,
  answers: {},
})
export const hasValue = (value) => value !== '' && value !== undefined && value !== null
export const money = (value) =>
  hasValue(value)
    ? `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
    : 'Not answered'
export const lifestyleEstimate = (planner) =>
  Math.ceil((Number(planner.monthlyExpenses.household) || 0) / 50000) * 50000
export const retirementEstimate = (planner, style) => {
  const base =
    (Number(planner.monthlyExpenses.household) || 0) +
    Number(planner.yearlyExpenses.lifestyle || lifestyleEstimate(planner)) / 12 +
    (Number(planner.yearlyExpenses.vacation) || 0) / 12
  return Math.round((base * ({ Frugal: 0.8, Maintain: 1, Premium: 1.2 }[style] || 1)) / 100) * 100
}

const numberValue = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0)

const monthYear = (date) => date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })

const ageFromDob = (dob, today = new Date()) => {
  if (!dob) return null
  const date = new Date(`${dob}T00:00:00`)
  if (Number.isNaN(date.getTime())) return null
  let age = today.getFullYear() - date.getFullYear()
  const month = today.getMonth() - date.getMonth()
  if (month < 0 || (month === 0 && today.getDate() < date.getDate())) age -= 1
  return age >= 0 ? age : null
}

const corpusForRecurringCost = (annualSpend, startAge, endAge, inflation, returnRate) => {
  if (annualSpend <= 0 || endAge <= startAge) return 0
  let corpus = 0
  for (let age = startAge; age < endAge; age += 1) {
    const years = age - startAge
    const inflatedSpend = annualSpend * (1 + inflation / 100) ** years
    corpus += inflatedSpend / (1 + returnRate / 100) ** years
  }
  return corpus
}

const roundedReserve = (value) => Math.ceil(Math.max(5000000, value) / 2500000) * 2500000

const yearsToReachInflatedTarget = (target, monthlyInvestment, annualReturn, annualInflation) => {
  if (target <= 0) return 0
  if (monthlyInvestment <= 0) return null
  const monthlyReturn = (1 + annualReturn / 100) ** (1 / 12) - 1
  const monthlyInflation = (1 + annualInflation / 100) ** (1 / 12) - 1
  for (let months = 1; months <= 12 * 80; months += 1) {
    const invested =
      monthlyReturn <= 0
        ? monthlyInvestment * months
        : (monthlyInvestment * ((1 + monthlyReturn) ** months - 1)) / monthlyReturn
    const required = target * (1 + monthlyInflation) ** months
    if (invested >= required) return months / 12
  }
  return null
}

const compoundedMonthlyInvestment = (monthlyInvestment, years, annualReturn) => {
  if (monthlyInvestment <= 0 || years <= 0) return 0
  const months = years * 12
  const monthlyReturn = (1 + annualReturn / 100) ** (1 / 12) - 1
  if (monthlyReturn <= 0) return monthlyInvestment * months
  return (monthlyInvestment * ((1 + monthlyReturn) ** months - 1)) / monthlyReturn
}

const inflatedAmount = (amount, years, annualInflation) =>
  amount * (1 + annualInflation / 100) ** years

const gapProjection = (target, monthlyInvestment, annualReturn, annualInflation, closureYears) => {
  if (target <= 0) return [{ year: 0, requiredAmount: 0, investedAmount: 0, progressPercent: 100 }]
  if (monthlyInvestment <= 0)
    return [{ year: 0, requiredAmount: target, investedAmount: 0, progressPercent: 0 }]
  const horizon = Math.max(1, Math.min(80, Math.ceil(closureYears || 10)))
  const years = Array.from({ length: horizon + 1 }, (_, index) => index)
  if (closureYears && !Number.isInteger(closureYears)) years.push(closureYears)
  return [...new Set(years)]
    .sort((a, b) => a - b)
    .map((projectionYear) => {
      const requiredAmount = inflatedAmount(target, projectionYear, annualInflation)
      const investedAmount = Math.min(
        requiredAmount,
        compoundedMonthlyInvestment(monthlyInvestment, projectionYear, annualReturn),
      )
      return {
        year: Math.round(projectionYear * 10) / 10,
        requiredAmount,
        investedAmount,
        progressPercent: requiredAmount
          ? Math.min(100, (investedAmount / requiredAmount) * 100)
          : 100,
      }
    })
}

/**
 * A deliberately conservative first pass based on the formulas in the source prototype.
 * It is kept pure so the same estimate can be rendered on the client and tested independently.
 */
export function calculateFirstPass(planner, today = new Date()) {
  const assumptions = {
    planTill: numberValue(planner.assumptions?.planTill) || 90,
    essentialInflation: numberValue(planner.assumptions?.essentialInflation) || 5,
    nonEssentialInflation: numberValue(planner.assumptions?.nonEssentialInflation) || 5,
    educationInflation: numberValue(planner.assumptions?.educationInflation) || 8,
    hybridReturn: 10,
    gapInvestmentReturn: 11,
  }
  const year = today.getFullYear()
  const planningMembers = (planner.members || [])
    .filter((member) => ['Self', 'Spouse / Partner'].includes(member.relationship))
    .map((member) => ({ ...member, age: ageFromDob(member.dob, today) }))
    .filter((member) => member.age !== null)
    .sort((a, b) => a.age - b.age)
  const currentAge = planningMembers.length ? planningMembers[0].age : 33
  const monthlyGross = (planner.salaries || []).reduce(
    (sum, income) => sum + numberValue(income.gross),
    0,
  )
  const monthlyDeductions = (planner.salaries || [])
    .filter((income) => income.incomeType === 'Salary')
    .reduce(
      (sum, income) =>
        sum + numberValue(income.tax) + numberValue(income.pf) + numberValue(income.nps),
      0,
    )
  const monthlyNetIncome = Math.max(0, monthlyGross - monthlyDeductions)
  const housingEmi = numberValue(planner.monthlyExpenses?.emi)
  const monthlyLoanEmiTotal = (planner.loans || []).reduce((sum, loan) => {
    if (loan.type === 'Home loan' && housingEmi > 0) return sum
    return sum + numberValue(loan.emi)
  }, 0)
  const monthlyExpenseTotal =
    Object.values(planner.monthlyExpenses || {}).reduce(
      (sum, value) => sum + numberValue(value),
      0,
    ) + monthlyLoanEmiTotal
  const selectedAnnualKeys = new Set(planner.annualSelected || [])
  const annualExpenseTotal =
    Object.entries(planner.yearlyExpenses || {}).reduce(
      (sum, [key, value]) =>
        sum + (key === 'lifestyle' || selectedAnnualKeys.has(key) ? numberValue(value) : 0),
      0,
    ) + (planner.extraYearlyExpenses || []).reduce((sum, item) => sum + numberValue(item.amount), 0)
  const monthlySurplus = Math.max(
    0,
    monthlyNetIncome - monthlyExpenseTotal - annualExpenseTotal / 12,
  )

  const retirementMonthly = numberValue(planner.retirement?.monthly) + annualExpenseTotal / 12
  const retirementCorpus = corpusForRecurringCost(
    retirementMonthly * 12,
    currentAge,
    assumptions.planTill,
    assumptions.essentialInflation,
    assumptions.hybridReturn,
  )
  const goalFunding = (planner.assetGoals || [])
    .filter((goal) => numberValue(goal.amount) > 0 && goal.kind !== 'Retirement')
    .map((goal) => {
      const years = Math.max(0, numberValue(goal.year) - year)
      const futureValue =
        numberValue(goal.amount) * (1 + assumptions.nonEssentialInflation / 100) ** years
      return {
        name: goal.name || goal.kind || 'One-time goal',
        year: numberValue(goal.year) || year,
        presentValue: futureValue / (1 + assumptions.hybridReturn / 100) ** years,
      }
    })
  const goalCorpus = goalFunding.reduce((sum, goal) => sum + goal.presentValue, 0)

  const childFees = numberValue(planner.yearlyExpenses?.childrenFees)
  const childCorpus = (planner.members || [])
    .filter((member) => member.relationship === 'Child')
    .reduce((sum, member) => {
      const age = ageFromDob(member.dob, today)
      const endAge = age === null ? currentAge + 18 : Math.min(25, age + 18)
      return (
        sum +
        corpusForRecurringCost(
          childFees,
          age === null ? currentAge : age,
          endAge,
          assumptions.educationInflation,
          assumptions.hybridReturn,
        )
      )
    }, 0)
  const parentSpend =
    planner.parentsSupport === 'Neither' ? 0 : numberValue(planner.monthlyExpenses?.parents) * 12
  const parentCorpus = corpusForRecurringCost(
    parentSpend,
    currentAge,
    assumptions.planTill,
    assumptions.essentialInflation,
    assumptions.hybridReturn,
  )
  const baseRequirement = retirementCorpus + goalCorpus + childCorpus + parentCorpus
  const emergencyReserve = monthlyExpenseTotal * 6
  const contingencyReserve = roundedReserve(baseRequirement * 0.1)
  const assetReplacementReserve = roundedReserve(baseRequirement * 0.1)
  const totalRequiredToday =
    baseRequirement + emergencyReserve + contingencyReserve + assetReplacementReserve
  const holdings = (planner.holdings || []).reduce(
    (sum, holding) => sum + numberValue(holding.value),
    0,
  )
  const loans = (planner.loans || []).reduce((sum, loan) => sum + numberValue(loan.amount), 0)
  const currentNetWorth = holdings - loans
  const gapToday = Math.max(0, totalRequiredToday - currentNetWorth)
  const gapClosureYears = yearsToReachInflatedTarget(
    gapToday,
    monthlySurplus,
    assumptions.gapInvestmentReturn,
    assumptions.essentialInflation,
  )
  const projection = gapProjection(
    gapToday,
    monthlySurplus,
    assumptions.gapInvestmentReturn,
    assumptions.essentialInflation,
    gapClosureYears,
  )
  const gapClosureMonths =
    gapClosureYears === null ? null : Math.max(0, Math.ceil(gapClosureYears * 12))
  const gapClosureDate =
    gapClosureMonths === null
      ? null
      : new Date(today.getFullYear(), today.getMonth() + gapClosureMonths, today.getDate())
  const gapClosureAge =
    gapClosureDate === null
      ? null
      : planningMembers[0]?.dob
      ? ageFromDob(planningMembers[0].dob, gapClosureDate)
      : Math.floor(currentAge + gapClosureMonths / 12)
  const futureGapTarget =
    gapClosureYears === null
      ? null
      : inflatedAmount(gapToday, gapClosureYears, assumptions.essentialInflation)
  const breakdown = [
    { key: 'retirement', label: 'Retirement income', value: retirementCorpus },
    ...(goalCorpus ? [{ key: 'goals', label: 'One-time goals', value: goalCorpus }] : []),
    ...(childCorpus
      ? [{ key: 'children', label: 'Children’s education', value: childCorpus }]
      : []),
    ...(parentCorpus ? [{ key: 'parents', label: 'Parent support', value: parentCorpus }] : []),
    { key: 'emergency', label: 'Emergency reserve', value: emergencyReserve },
    { key: 'contingency', label: 'Contingency reserve', value: contingencyReserve },
    { key: 'replacement', label: 'Asset replacement reserve', value: assetReplacementReserve },
  ]
  return {
    currentAge,
    monthlyGross,
    monthlyDeductions,
    monthlyNetIncome,
    monthlyExpenseTotal,
    annualExpenseTotal,
    monthlySurplus,
    monthlyLoanEmiTotal,
    retirementCorpus,
    goalFunding,
    goalCorpus,
    childCorpus,
    parentCorpus,
    emergencyReserve,
    contingencyReserve,
    assetReplacementReserve,
    baseRequirement,
    totalRequiredToday,
    holdings,
    loans,
    currentNetWorth,
    gapToday,
    futureGapTarget,
    gapClosureYears,
    gapClosureMonths,
    gapClosureYear: gapClosureDate?.getFullYear() || null,
    gapClosureMonth: gapClosureDate ? monthYear(gapClosureDate) : null,
    gapClosureAge,
    gapProjection: projection,
    breakdown,
    assumptions,
  }
}
// Keep earlier answers, but don't reuse a numeric position after changing the question sequence.
export function restorePlanner(saved) {
  const base = createPlanner()
  const next = { ...base, ...saved }
  for (const section of [
    'monthlyExpenses',
    'yearlyExpenses',
    'insurance',
    'retirement',
    'assumptions',
    'answers',
  ])
    next[section] = { ...base[section], ...saved[section] }
  for (const section of [
    'members',
    'salaries',
    'holdings',
    'loans',
    'assetGoals',
    'extraYearlyExpenses',
  ])
    next[section] = Array.isArray(saved[section]) ? saved[section] : base[section]
  if (!next.members.length) next.members = base.members
  if (saved.questionnaire2Version !== 2) {
    next.questionnaire2Step = 'welcome'
    next.annualSelected = ANNUAL_COSTS.filter(([key]) => hasValue(next.yearlyExpenses[key])).map(
      ([key]) => key,
    )
    next.annualChoice = next.annualSelected.length ? 'Selected' : ''
    // Old rent values combined rent and EMI; ask the user to classify the amount.
    if (hasValue(next.monthlyExpenses.rent)) next.legacyHousingAmount = next.monthlyExpenses.rent
    next.monthlyExpenses.rent = ''
    next.monthlyExpenses.housing = ''
  }
  next.questionnaire2Version = 2
  return next
}
export function cleanPlanner(planner) {
  const next = {
    ...planner,
    salaries: planner.salaries.map((income) =>
      income.incomeType === 'Salary' ? income : { ...income, tax: '', pf: '', nps: '' },
    ),
  }
  next.monthlyExpenses = {
    ...planner.monthlyExpenses,
    rent: ['Rent', 'Both'].includes(planner.monthlyExpenses.housing)
      ? planner.monthlyExpenses.rent
      : '',
    emi: ['Home-loan EMI', 'Both'].includes(planner.monthlyExpenses.housing)
      ? planner.monthlyExpenses.emi
      : '',
    parents: planner.parentsSupport === 'Neither' ? '' : planner.monthlyExpenses.parents,
    children: planner.monthlyExpenses.children,
  }
  next.loans = (planner.loans || []).filter(
    (loan) => hasValue(loan.amount) || hasValue(loan.emi) || hasValue(loan.monthsLeft),
  )
  next.insurance = { ...planner.insurance }
  for (const [answer, prefix] of [
    ['corporateMedical', 'corporate'],
    ['privateMedical', 'private'],
    ['term', 'term'],
  ]) {
    if (next.insurance[answer] !== 'Yes')
      for (const suffix of ['Coverage', 'Who', 'Basis']) next.insurance[`${prefix}${suffix}`] = ''
  }
  next.yearlyExpenses = { ...planner.yearlyExpenses }
  const applicableKeys = applicableAnnualCosts(planner).map(([key]) => key)
  next.annualSelected = planner.annualSelected.filter((key) => applicableKeys.includes(key))
  for (const [key] of ANNUAL_COSTS)
    if (!next.annualSelected.includes(key)) next.yearlyExpenses[key] = ''
  next.estimates = calculateFirstPass(next)
  return next
}

export const applicableAnnualCosts = (planner) =>
  ANNUAL_COSTS.filter(
    ([key]) =>
      key !== 'childrenFees' || planner.members.some((member) => member.relationship === 'Child'),
  )
